import React, { createContext, useContext, useState, useEffect } from 'react';

const CookieContext = createContext();

export const useCookieConsent = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieProvider');
  }
  return context;
};

export const CookieProvider = ({ children }) => {
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Sempre true, non modificabile
    analytics: false,
    marketing: false,
    functionality: false,
    personalization: false
  });
  
  const [showBanner, setShowBanner] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Carica le preferenze salvate al mount
  useEffect(() => {
    const savedPreferences = localStorage.getItem('cookie-preferences');
    const hasConsented = localStorage.getItem('cookie-consent-given');
    const anonymousSessionId = localStorage.getItem('anonymous-session-id');
    
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        setCookiePreferences(parsed);
        
        // Emetti evento per aggiornare Google Analytics con le preferenze salvate
        window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
          detail: { preferences: parsed } 
        }));
      } catch (error) {
        console.error('Error parsing saved cookie preferences:', error);
      }
    }
    
    // Mostra banner solo se non ha mai dato il consenso
    if (!hasConsented) {
      setShowBanner(true);
    }
    
    // Se l'utente ha preferenze anonime e si è appena loggato, associale
    if (hasConsented && anonymousSessionId && savedPreferences) {
      associateAnonymousPreferences(anonymousSessionId, savedPreferences);
    }
    
    setIsInitialized(true);
  }, []);

  // Funzione per associare preferenze anonime a un utente autenticato
  const associateAnonymousPreferences = async (sessionId, preferences) => {
    try {
      await fetch('/api/cookie/associate-anonymous-preferences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          anonymousSessionId: sessionId,
          preferences: JSON.parse(preferences)
        })
      });
      
      // Rimuovi il session ID anonimo dopo l'associazione
      localStorage.removeItem('anonymous-session-id');
    } catch (error) {
      console.error('Error associating anonymous preferences:', error);
    }
  };

  // Salva le preferenze nel localStorage e nel backend
  const savePreferences = async (preferences) => {
    try {
      // Genera un session ID anonimo se non esiste
      let anonymousSessionId = localStorage.getItem('anonymous-session-id');
      if (!anonymousSessionId) {
        anonymousSessionId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('anonymous-session-id', anonymousSessionId);
      }
      
      // Salva localmente
      localStorage.setItem('cookie-preferences', JSON.stringify(preferences));
      localStorage.setItem('cookie-consent-given', 'true');
      
      // Salva nel backend per compliance GDPR
      await fetch('/api/cookie/cookie-preferences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          preferences,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent + ' [SessionID: ' + anonymousSessionId + ']',
          ipAddress: null // Sarà gestito dal backend
        })
      });
      
      setCookiePreferences(preferences);
      setShowBanner(false);
      
      // Emetti un evento personalizzato per aggiornare Google Analytics
      window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
        detail: { preferences } 
      }));
    } catch (error) {
      console.error('Error saving cookie preferences:', error);
      // Fallback: salva solo localmente
      localStorage.setItem('cookie-preferences', JSON.stringify(preferences));
      localStorage.setItem('cookie-consent-given', 'true');
      setCookiePreferences(preferences);
      setShowBanner(false);
      
      // Emetti un evento personalizzato anche in caso di errore
      window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
        detail: { preferences } 
      }));
    }
  };

  // Accetta tutti i cookie
  const acceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      functionality: true,
      personalization: true
    };
    savePreferences(allAccepted);
  };

  // Accetta solo essenziali
  const acceptEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      functionality: false,
      personalization: false
    };
    savePreferences(essentialOnly);
  };

  // Salva preferenze personalizzate
  const saveCustomPreferences = (customPreferences) => {
    const newPreferences = {
      ...cookiePreferences,
      ...customPreferences,
      essential: true // Essenziali sempre true
    };
    savePreferences(newPreferences);
  };

  // Controlla se un tipo di cookie è accettato
  const isAccepted = (type) => {
    return cookiePreferences[type] || false;
  };

  // Controlla se tutti i cookie sono accettati
  const areAllAccepted = () => {
    return Object.values(cookiePreferences).every(value => value === true);
  };

  const value = {
    cookiePreferences,
    showBanner,
    isInitialized,
    acceptAll,
    acceptEssential,
    saveCustomPreferences,
    isAccepted,
    areAllAccepted,
    setShowBanner
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
}; 